import { useState, useRef } from 'react'
import { Upload, X, Image as ImageIcon } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { toast } from 'sonner'
import Button from './Button'

interface ImageUploadProps {
  value: string // URL de l'image actuelle
  onChange: (url: string) => void
  label?: string
  folder?: string // Sous-dossier dans le bucket
  bucket?: string // Nom du bucket Supabase
  required?: boolean
}

function ImageUpload({ value, onChange, label = 'Image', folder = 'guitars', bucket = 'instruments', required = false }: ImageUploadProps) {
  const [uploading, setUploading] = useState(false)
  const [preview, setPreview] = useState<string>(value)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Vérifier le type de fichier
    if (!file.type.startsWith('image/')) {
      toast.error('Le fichier doit être une image')
      return
    }

    // Vérifier la taille (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('L\'image ne doit pas dépasser 5MB')
      return
    }

    setUploading(true)

    try {
      // Générer un nom de fichier unique
      const fileExt = file.name.split('.').pop()
      const fileName = `${folder}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`

      // Upload vers Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        })

      if (uploadError) throw uploadError

      // Obtenir l'URL publique
      const { data: urlData } = supabase.storage
        .from(bucket)
        .getPublicUrl(fileName)

      const publicUrl = urlData.publicUrl

      // Mettre à jour la preview et notifier le parent
      setPreview(publicUrl)
      onChange(publicUrl)

      toast.success('Image uploadée avec succès')
    } catch (error: any) {
      console.error('Error uploading image:', error)
      toast.error(error.message || 'Erreur lors de l\'upload')
    } finally {
      setUploading(false)
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleRemove = () => {
    setPreview('')
    onChange('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleURLInput = (url: string) => {
    setPreview(url)
    onChange(url)
  }

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      {/* Preview */}
      {preview && (
        <div className="relative inline-block">
          <div className="w-48 h-48 bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200">
            <img
              src={preview}
              alt="Preview"
              className="w-full h-full object-contain"
            />
          </div>
          <button
            type="button"
            onClick={handleRemove}
            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Upload button */}
      <div className="flex gap-3">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          id={`file-upload-${label}`}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {uploading ? (
            <>
              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
              Upload en cours...
            </>
          ) : (
            <>
              <Upload className="h-4 w-4" />
              Uploader une image
            </>
          )}
        </button>

        {!preview && (
          <Button
            type="button"
            variant="outline"
            leftIcon={<ImageIcon className="h-4 w-4" />}
            onClick={() => {
              const url = prompt('Ou entrez l\'URL d\'une image :')
              if (url) handleURLInput(url)
            }}
          >
            URL externe
          </Button>
        )}
      </div>

      {/* URL input (always show for editing) */}
      <input
        type="url"
        value={preview}
        onChange={(e) => handleURLInput(e.target.value)}
        placeholder="Ou collez l'URL d'une image..."
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
        required={required && !preview}
      />

      <p className="text-xs text-gray-500">
        Formats acceptés : JPG, PNG, WebP • Taille max : 5MB
      </p>
    </div>
  )
}

export default ImageUpload
